import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Sparkles, Lock, AlertTriangle, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StudentActivationPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activated, setActivated] = useState(false);

  const handleActivate = async (e) => {
    e.preventDefault();
    setError('');

    if (!password || password.length < 6) {
      setError('Password must be at least 6 characters'); return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match'); return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/activate-student/${token}`, { password });
      toast.success('Account activated! Welcome to Agentic FuturePath AI');
      setActivated(true);

      // Auto-login after 2 seconds
      setTimeout(() => {
        localStorage.setItem('futurepath_token', res.data.access_token);
        window.location.href = '/app/dashboard';
      }, 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Activation failed. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (activated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
        <div className="text-center space-y-6 max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto">
            <Check className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome to Agentic FuturePath AI!</h1>
          <p className="text-slate-300">Your account is activated. Redirecting you to your dashboard...</p>
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-xl bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-7 h-7 text-blue-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Activate Your Account</h1>
          <p className="text-slate-300 text-sm mt-2">
            Your parent/guardian has set up a Agentic FuturePath AI account for you.
            Set your password to get started!
          </p>
        </div>

        <form onSubmit={handleActivate} className="glass-card p-6 border border-slate-700 space-y-4" data-testid="activation-form">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2" data-testid="activation-error">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          <div>
            <label className="text-sm text-slate-300 block mb-1">Create Your Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 characters" data-testid="activation-password" />
          </div>
          <div>
            <label className="text-sm text-slate-300 block mb-1">Confirm Password</label>
            <Input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Type it again" data-testid="activation-confirm-password" />
          </div>

          <Button type="submit" disabled={loading} className="w-full" data-testid="activation-submit-btn">
            {loading ? 'Activating...' : <><Lock className="w-4 h-4 mr-2" /> Activate My Account</>}
          </Button>

          <div className="pt-4 p-4 bg-slate-800/50 rounded-lg space-y-2">
            <p className="text-sm font-medium text-white">What Agentic FuturePath AI does for you:</p>
            <ul className="text-sm text-slate-300 space-y-1">
              <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" /> Explore careers that match your interests and strengths</li>
              <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" /> Build your academic portfolio with AI-powered insights</li>
              <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" /> Get personalized college and scholarship recommendations</li>
              <li className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-blue-400 mt-0.5 shrink-0" /> Track your progress with milestone achievements</li>
            </ul>
          </div>
        </form>
      </div>
    </div>
  );
}
